/**
 * Compliance badge shown on an asset card in the pending view.
 *
 * Surfaces the asset's rule verdicts at a glance: a severity-coloured warning icon
 * when rules are violated (red for the most severe BLOCKER, amber for WARNING, a
 * neutral sign for INFO) and a separate icon when one or more rules could not be
 * evaluated (or the asset was never evaluated). Hovering either icon lists the
 * relevant rules as "name: reason", or just the name when no reason was recorded.
 */

import React from 'react';
import Column from '@amzn/meridian/column';
import Icon from '@amzn/meridian/icon';
import Text from '@amzn/meridian/text';
import Tooltip from '@amzn/meridian/tooltip';
import alertTokens from '@amzn/meridian-tokens/base/icon/alert';
import checkCircleTokens from '@amzn/meridian-tokens/base/icon/check-circle';
import helpKnockoutTokens from '@amzn/meridian-tokens/base/icon/help-knockout';
import { useTranslation } from 'src/arb-wrapper/useTranslation';
import type { EvaluatedRule, RuleMap } from 'src/types/assetEval';
import {
  computeAssetEvalDisplay,
  formatRuleLine,
  severityIconColor,
} from 'src/utils/assetEvalBadge';

export interface AssetEvalBadgeProps {
  assetId: string;
  evaluatedRules: EvaluatedRule[] | undefined;
  ruleMap: RuleMap;
}

const TooltipLines = ({ heading, lines }: { heading: string; lines: string[] }) => (
  <Column spacing="100">
    <Text color="inverted" type="b100">{heading}</Text>
    {lines.map((line, index) => (
      <Text color="inverted" key={`${line}-${index}`} type="b100">
        {line}
      </Text>
    ))}
  </Column>
);

export const AssetEvalBadge = ({ assetId, evaluatedRules, ruleMap }: AssetEvalBadgeProps) => {
  const { t } = useTranslation();
  const display = computeAssetEvalDisplay(evaluatedRules, ruleMap);

  if (!display.hasViolations && !display.hasFailure && !display.passed) {
    return null;
  }

  // The failure icon represents a real evaluation error (a FAILED verdict). Show only which
  // rule could not be evaluated — never the raw exception, which is internal detail (it is
  // still stamped on the verdict in the backend for internal viewing).
  const failureLines = display.failed.map((rule) => rule.name);

  return (
    <>
      {display.hasViolations && (
        <Tooltip
          id={`asset-eval-violations-${assetId}`}
          position="end"
          title={<TooltipLines heading={t('asset_eval_violations_title')} lines={display.violated.map(formatRuleLine)} />}
        >
          <Icon
            color={severityIconColor(display.worstSeverity)}
            data-testid="asset-eval-violation-icon"
            tokens={alertTokens}
          />
        </Tooltip>
      )}
      {display.hasFailure && (
        <Tooltip
          id={`asset-eval-failed-${assetId}`}
          position="end"
          title={<TooltipLines heading={t('asset_eval_failed_title')} lines={failureLines} />}
        >
          <Icon data-testid="asset-eval-failed-icon" tokens={helpKnockoutTokens} />
        </Tooltip>
      )}
      {display.passed && (
        <Tooltip
          id={`asset-eval-passed-${assetId}`}
          position="end"
          title={t('asset_eval_passed_title')}
        >
          <Icon color="success" data-testid="asset-eval-passed-icon" tokens={checkCircleTokens} />
        </Tooltip>
      )}
    </>
  );
};
